import { m } from "#/paraglide/messages";
import { Button, cn } from "@beweegbaas/ui";
import { Link, useRouteContext } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import type { ReactNode } from "react";
import type { EnvironmentInfo } from "#/modules/util.environment/env.server";
import { useEnvironment } from "#/modules/util.environment/use-environment";
import { useLocaleParams, useSchoolParams } from "#/modules/util.helpers/route-href";
import { stopImpersonation } from "#/modules/util.impersonate/impersonate.functions";
import { useIsAdmin, useIsImpersonating } from "#/modules/util.auth/use-user";

/** Visible thickness of the env viewport frame. Page padding and the fixed sidebar read this. */
export const ENV_CHROME_INSET = "5px";

/** True while the session is an admin one (admin browsing, impersonating, or forced via `always`). */
export function useIsAdminSession(always = false) {
  const isAdmin = useIsAdmin();
  const isImpersonating = useIsImpersonating();
  return always || isAdmin || isImpersonating;
}

/** Env signal bg: PROD red, ACC orange, TEST green, DEV blue. */
function envBgClass(env: EnvironmentInfo) {
  if (env.isProduction) return "bg-linear-to-br from-red-700 to-red-800";
  if (env.isAcceptance) return "bg-linear-to-br from-orange-500 to-orange-400";
  if (env.isTest) return "bg-linear-to-br from-green-600 to-green-800";
  return "bg-linear-to-br from-blue-600 to-blue-500";
}

function envFrameShadowClass(env: EnvironmentInfo) {
  if (env.isProduction) return "shadow-[0_0_0_2rem_var(--color-red-700)]";
  if (env.isAcceptance) return "shadow-[0_0_0_2rem_var(--color-orange-500)]";
  if (env.isTest) return "shadow-[0_0_0_2rem_var(--color-green-700)]";
  return "shadow-[0_0_0_2rem_var(--color-blue-600)]";
}

function envShortLabel(env: EnvironmentInfo) {
  if (env.isProduction) return "PROD";
  if (env.isAcceptance) return "ACC";
  if (env.isTest) return "TEST";
  return "DEV";
}

/**
 * Env-colored viewport frame (fixed, scroll-independent). Rendered `always` in the admin
 * section; in the school scope it shows while the session is still an admin one
 * (impersonating or admin-browsing). Session flags come from the ROOT ROUTER CONTEXT so
 * impersonation start/stop flips are picked up immediately.
 */
export function EnvChrome({ always = false }: { always?: boolean }) {
  const env = useEnvironment();
  const show = useIsAdminSession(always);
  if (!show) return null;

  // Frame shadow for the viewport box: a huge zero-blur spread around a rounded rect paints
  // the area between the rect and the viewport edge — sharp outer corners, rounded inner
  // corners (not expressible with a plain border).
  return (
    <div
      aria-hidden
      className={cn(
        "squircle pointer-events-none fixed inset-[var(--env-chrome-inset,5px)] z-50 rounded-xl",
        envFrameShadowClass(env),
      )}
    />
  );
}

/**
 * Sets `--env-chrome-inset` so the sidebar shell can pad the page inside the env frame
 * (see `SidebarProvider` / the fixed sidebar container).
 */
export function EnvChromeShell({
  always = false,
  children,
}: {
  always?: boolean;
  children: ReactNode;
}) {
  const show = useIsAdminSession(always);
  return (
    <div style={{ "--env-chrome-inset": show ? ENV_CHROME_INSET : "0px" }}>
      <EnvChrome always={always} />
      {children}
    </div>
  );
}

/**
 * Sidebar env + session chrome: one env-colored button with the env/session label and an
 * exit icon on the right when impersonating or admin-viewing. Hides in icon-collapsed mode.
 * Same visibility rules as EnvChrome (`always` for the admin section).
 */
export function AdminSidebarBanner({ always = false }: { always?: boolean }) {
  const isAdmin = useIsAdmin();
  const isImpersonating = useIsImpersonating();
  const env = useEnvironment();
  const { school } = useSchoolParams();
  const hideImpersonationBanner = useRouteContext({
    from: "__root__",
    select: (ctx) => ctx.hideImpersonationBanner,
  });
  const localeLinkParams = useLocaleParams();
  const stopImpersonationFn = useServerFn(stopImpersonation);

  const show = useIsAdminSession(always);
  if (!show) return null;

  const className = cn(
    `squircle flex w-full items-center gap-1 rounded-xl px-3 py-2 text-xs text-white
    font-black font-heading hover:opacity-90 group-data-[collapsible=icon]:hidden`,
    envBgClass(env),
  );

  if (isImpersonating && !hideImpersonationBanner) {
    return (
      <Button
        variant="reset"
        className={className}
        contentClassName="w-full"
        aria-label="Stop impersonating"
        onClick={() => {
          void stopImpersonationFn({ data: { locale: localeLinkParams.locale } });
        }}
      >
        {env.isProduction && (
          <IconMaterialSymbolsWarningOutlineRounded className="size-3.5 shrink-0" />
        )}
        <span className="grow truncate text-left">{envShortLabel(env)} - Impersonating</span>
        <IconMdiExitToApp className="shrink-0" />
      </Button>
    );
  }

  if (isAdmin && school && !isImpersonating) {
    return (
      <Button
        variant="reset"
        className={className}
        contentClassName="w-full"
        aria-label={m["admin.terug_naar_admin_aria"]()}
        render={<Link to="/$locale/{-$schoolyear}/admin/schools" params={localeLinkParams} />}
        nativeButton={false}
      >
        {env.isProduction && (
          <IconMaterialSymbolsWarningOutlineRounded className="size-3.5 shrink-0" />
        )}
        <span className="grow truncate text-left">{envShortLabel(env)} - admin viewing</span>
        <IconMdiExitToApp className="shrink-0" />
      </Button>
    );
  }

  return (
    <div className={className}>
      {env.isProduction && (
        <IconMaterialSymbolsWarningOutlineRounded className="size-3.5 shrink-0" />
      )}
      <span className="grow truncate text-center">{envShortLabel(env)}</span>
    </div>
  );
}
